import { CATEGORY_PATH, CATEGORIES, LOCALES, type Category } from '@ai-radar/shared';
import { getSitemap } from '@/lib/api';
import { absoluteUrl } from '@/lib/site';

/**
 * Sitemap with hreflang alternates.
 *
 * Hand-written XML rather than Next's sitemap helper: every URL needs
 * `xhtml:link` alternates for both locales, and the helper's `alternates`
 * support does not express x-default cleanly.
 *
 * Only pages with real content are listed. A thin page is an SEO
 * liability, not an asset — the API already filters companies to those
 * with at least three posts.
 */
export const revalidate = 3600;

interface Entry {
  /** Path WITHOUT the locale prefix. */
  path: string;
  lastmod?: string;
  changefreq: 'hourly' | 'daily' | 'weekly';
  priority: string;
}

export async function GET(): Promise<Response> {
  const data = await getSitemap();

  const entries: Entry[] = [
    { path: '', changefreq: 'hourly', priority: '1.0' },
    ...CATEGORIES.map(
      (category): Entry => ({
        path: `/${CATEGORY_PATH[category]}`,
        changefreq: 'daily',
        priority: '0.8',
      }),
    ),
    { path: '/about', changefreq: 'weekly', priority: '0.5' },
    { path: '/privacy', changefreq: 'weekly', priority: '0.4' },
    { path: '/delete-account', changefreq: 'weekly', priority: '0.3' },
    ...data.posts.map((post): Entry => {
      const category = post.category as Category;
      return {
        path: `/${CATEGORY_PATH[category] ?? 'news'}/${post.slug}`,
        lastmod: isoDate(post.updated_at ?? post.published_at),
        changefreq: 'weekly',
        priority: '0.7',
      };
    }),
    ...data.companies.map(
      (company): Entry => ({
        path: `/companies/${company.slug}`,
        changefreq: 'weekly',
        priority: '0.5',
      }),
    ),
  ];

  const urls = entries
    .flatMap((entry) =>
      LOCALES.map((locale) => {
        const loc = absoluteUrl(`/${locale}${entry.path}`);

        // Every URL declares all locales plus x-default, including a
        // self-reference — that is what Google requires for the
        // cluster to be recognised.
        const alternates = [
          ...LOCALES.map(
            (alt) =>
              `    <xhtml:link rel="alternate" hreflang="${alt}" href="${escapeXml(
                absoluteUrl(`/${alt}${entry.path}`),
              )}"/>`,
          ),
          `    <xhtml:link rel="alternate" hreflang="x-default" href="${escapeXml(
            absoluteUrl(`/en${entry.path}`),
          )}"/>`,
        ].join('\n');

        return [
          '  <url>',
          `    <loc>${escapeXml(loc)}</loc>`,
          entry.lastmod ? `    <lastmod>${entry.lastmod}</lastmod>` : null,
          `    <changefreq>${entry.changefreq}</changefreq>`,
          `    <priority>${entry.priority}</priority>`,
          alternates,
          '  </url>',
        ]
          .filter(Boolean)
          .join('\n');
      }),
    )
    .join('\n');

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"
        xmlns:xhtml="http://www.w3.org/1999/xhtml">
${urls}
</urlset>`;

  return new Response(xml, {
    headers: {
      'content-type': 'application/xml; charset=utf-8',
      'cache-control': 'public, max-age=3600, s-maxage=3600',
    },
  });
}

function isoDate(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
